import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { MODEL_VERSION } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type { FormParams } from '@/app/stores/slices/task-form';
import { validateRecaptionPrompt } from '@/app/utils';
import type { FormTabType } from '../types';

/** 会触发升级弹窗的模型版本 */
type UpgradeModelVersion = typeof MODEL_VERSION.Q1 | typeof MODEL_VERSION.Q3_A | typeof MODEL_VERSION.Q3_HH;

interface UseFormValidationOptions {
  taskType: Exclude<FormTabType, 'scene'>;
  formParams: FormParams;
  onOpenPlanDialog?: (fromPage?: string) => void;
}

/** 升级弹窗 from_page 中的模型标识，键与 upgradeInfo 的判定条件一一对应 */
const UPGRADE_MODEL_KEY_MAP: Record<UpgradeModelVersion, string> = {
  [MODEL_VERSION.Q1]: 'q1',
  [MODEL_VERSION.Q3_A]: 'q3a',
  [MODEL_VERSION.Q3_HH]: 'q3hh',
};

export const useFormValidation = ({ taskType, formParams, onOpenPlanDialog }: UseFormValidationOptions) => {
  const t = useTranslations();

  const { modelVersion, userInfo, remainingTrialsQ1 } = useStore((state) => ({
    modelVersion: state[`${taskType}FormParams`].modelVersion,
    userInfo: state.userInfo,
    remainingTrialsQ1: state.trialsQ1?.remaining,
  }));

  const isFree = userInfo?.subs_plan === 'free';

  // 需要升级才能使用当前模型时，记录模型与原因，供埋点区分场景（按钮可点，点击时弹升级弹窗）
  // model_locked：模型本身需要付费；trial_exhausted：Q1 免费试用次数已用尽
  const upgradeInfo = useMemo<{ model: UpgradeModelVersion; reason: string } | null>(() => {
    if (!isFree) return null;
    if (modelVersion === MODEL_VERSION.Q3_A || modelVersion === MODEL_VERSION.Q3_HH) {
      return { model: modelVersion, reason: 'model_locked' };
    }
    if (
      taskType !== 'reference2image' &&
      modelVersion === MODEL_VERSION.Q1 &&
      typeof remainingTrialsQ1 === 'number' &&
      remainingTrialsQ1 <= 0
    ) {
      return { model: modelVersion, reason: 'trial_exhausted' };
    }
    return null;
  }, [modelVersion, isFree, taskType, remainingTrialsQ1]);

  const shouldUpgradeForModel = upgradeInfo !== null;

  const errorMessage = useMemo(() => {
    const isProMode = formParams.isProMode;

    // 专业模式需要检查所有必填 key
    if (isProMode && !validateRecaptionPrompt(formParams.recaptionPrompt)) {
      return t('create.createForm.error.recaptionRequiredFields');
    }

    return '';
  }, [formParams, t]);

  // 上报 from_page：{模型}_{表单类型}_{原因}，与 generateFromPage 的埋点命名风格保持一致，
  // 便于 BI 区分是哪个模型、哪个创作页、因何触发升级弹窗（原值 'create_button' 无法区分场景）
  const handleUpgradeClick = () => {
    if (!upgradeInfo) return;
    onOpenPlanDialog?.(`${UPGRADE_MODEL_KEY_MAP[upgradeInfo.model]}_${taskType}_${upgradeInfo.reason}`);
  };

  return { errorMessage, shouldUpgradeForModel, handleUpgradeClick };
};
